import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  getLeadsForPicker,
  createLead,
  getLeadsByStatus,
  getAllAccommodationsByProject,
  getProjectByID
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import Multiselect from 'multiselect-react-dropdown';
import { Controller } from 'react-hook-form';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import {
  getCountryCodeOptions,
  validatePhoneNumber,
  getPhoneNumberLength,
  cleanPhoneNumber,
  getDefaultCountryCode,
  getCountryCallingCode
  // detectCountryFromPhoneNumber // No longer used - auto-detection disabled
} from '../../utils/phoneNumberUtils';

// Validation schema for lead creation
const createLeadSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  countryCode: yup.string().required('Country code is required'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .test('phone-validation', 'Invalid phone number for selected country', function (value) {
      const { countryCode } = this.parent;
      if (!value || !countryCode) return true;
      return validatePhoneNumber(value, countryCode);
    }),
  alternate_phone_number: yup
    .string()
    .test('phone-validation', 'Invalid phone number for selected country', function (value) {
      const { countryCode } = this.parent;
      if (!value || !countryCode) return true;
      return validatePhoneNumber(value, countryCode);
    }),
  occupation: yup.string(),
  address: yup.string(),
  reference_name: yup.string(),
  email: yup.string(),
  gender: yup.string(),
  interested_projects: yup.string().required('Select atleat one project'),
  accomodations: yup.array(),
  plotArea: yup.array(),
  project_accommodations: yup.array(),
  expectedBudgetRange: yup.string(),
  source: yup.string()
});

export const SelectLead = ({
  onClose,
  onLeadSelect,
  isSubmitting,
  projectName,
  projectId,
  filterForUnderConsideration = false
}) => {
  const userId = Cookies.get('userId');
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [projectAccommodationsKey, setProjectAccommodationsKey] = useState(0);
  const multiselectRef = useRef(null);
  const [leadPickerSearch, setLeadPickerSearch] = useState('');
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState('');

  // Phone number utilities
  const [countryCodeOptions] = useState(() => getCountryCodeOptions());
  const [selectedCountryCode, setSelectedCountryCode] = useState(getDefaultCountryCode());
  const autoDetectTimeoutRef = useRef(null);

  // Form for selecting lead
  const {
    register: selectRegister,
    handleSubmit: handleSelectSubmit,
    formState: { errors: selectErrors }
  } = useForm({
    defaultValues: {
      lead_id: ''
    }
  });
  const {
    data: leadsData,
    isLoading: isLeadsLoading,
    refetch: refetchLeads
  } = useQuery({
    queryKey: [
      'leadsPicker',
      filterForUnderConsideration ? 'uc' : 'deal',
      debouncedLeadSearch,
      projectId
    ],
    queryFn: () =>
      getLeadsForPicker({
        page: 1,
        limit: 100,
        search: debouncedLeadSearch,
        excludeDeal: true,
        excludeUc: Boolean(filterForUnderConsideration),
        projectId: projectId || null
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedLeadSearch(leadPickerSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [leadPickerSearch]);

  // Form for creating new lead
  const {
    register: createRegister,
    handleSubmit: handleCreateSubmit,
    formState: { errors: createErrors },
    reset: resetCreateForm,
    setValue,
    control,
    watch
  } = useForm({
    resolver: yupResolver(createLeadSchema),
    defaultValues: {
      countryCode: getDefaultCountryCode(),
      interested_projects: projectId ? String(projectId) : '',
      accomodations: [],
      plotArea: [],
      project_accommodations: []
    }
  });

  // Phone number utilities that depend on useForm
  const watchedCountryCode = watch('countryCode');

  // Mutation for creating new lead
  const { mutate: createLeadMutation } = useMutation({
    mutationFn: (data) => createLead(data),
    onSuccess: () => {
      toast.success('Lead created successfully');
      setIsCreateLeadModalOpen(false);
      resetCreateForm();
      refetchLeads();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create lead');
    }
  });

  // Handle lead selection
  const onSubmitSelect = (data) => {
    const selectedLead = leadsData?.data?.find((lead) => lead.id === parseInt(data.lead_id));
    if (selectedLead) {
      onLeadSelect(selectedLead);
    } else {
      toast.error('Please select a lead');
    }
  };

  // Handle new lead creation
  const onSubmitCreate = (data) => {
    // Custom validation: at least one of accomodations or plotArea  must be selected
    // if (
    //   (!data.accomodations || data.accomodations.length === 0) &&
    //   (!data.plotArea || data.plotArea.length === 0)
    // ) {
    //   toast.error('Please select at least one Accomodation or Plot Area');
    //   return;
    // }
    // Construct clean payload with only required fields
    const payload = {
      name: data.name,
      countryCode: `+${getCountryCallingCode(data.countryCode)}`, // Pass calling code like "+91" instead of "IN"
      phone_number: data.phone_number,
      interested_projects: [Number(data?.interested_projects)]
    };

    // Log the payload for debugging
    console.log('📤 SelectLead - Payload being sent:', {
      originalCountryCode: data.countryCode,
      callingCode: `+${getCountryCallingCode(data.countryCode)}`,
      phone_number: data.phone_number
    });

    // Add optional fields only if they have values
    if (data.accomodations)
      payload.interestedAccomodationType =
        data?.accomodations?.map((item) => item.id || item) || [];
    if (data.plotArea) payload.plotArea = data?.plotArea?.map((item) => item.id || item) || [];
    if (data.project_accommodations)
      payload.accomodations = data?.project_accommodations?.map((item) => item.id || item) || [];
    if (data.email) payload.email = data.email;
    if (data.address) payload.address = data.address;
    if (data.alternate_phone_number) payload.alternate_phone_number = data.alternate_phone_number;
    if (data.occupation) payload.occupation = data.occupation;
    if (data.gender) payload.gender = data.gender;
    if (data.source) payload.source = data.source;
    if (data.expected_clouser_date) payload.expected_clouser_date = data.expected_clouser_date;
    if (data.expectedBudgetRange) payload.expectedBudgetRange = data.expectedBudgetRange;

    // Explicitly remove any unwanted keys that might exist
    delete payload['plot_area'];
    delete payload['plotArea '];

    createLeadMutation(payload);
  };

  const transformedData = [
    {
      label: projectName,
      id: projectId,
      value: projectId
    }
  ];

  const occupationOptions = [
    { value: 'Salaried', label: 'Salaried' },
    { value: 'Self employed', label: 'Self Employed' },
    { value: 'Business', label: 'Business' },
    { value: 'Govt employee', label: 'Government Employee' },
    { value: 'Others', label: 'Others' }
  ];

  // Transform leads data for dropdown (server already excludes deal / UC when requested)
  const leadOptions = [
    { value: '', label: 'Select Lead', disabled: true },
    ...(leadsData?.data
      ?.map((lead) => {
        const name = lead?.user?.name || 'Unnamed Lead';
        const isInactive = lead?.user && !lead.user.is_active;
        return {
          value: lead.id,
          label: isInactive ? `${name} (Inactive)` : name
        };
      }) || [])
  ];

  // Auto-detect country code from phone number - DISABLED
  // const debouncedAutoDetect = useCallback(
  //   (phoneNumber) => {
  //     if (autoDetectTimeoutRef.current) {
  //       clearTimeout(autoDetectTimeoutRef.current);
  //     }
  //     autoDetectTimeoutRef.current = setTimeout(() => {
  //       if (phoneNumber.length >= 7) {
  //         const detectedCountry = detectCountryFromPhoneNumber(phoneNumber);
  //         if (detectedCountry && detectedCountry !== watchedCountryCode) {
  //           setValue('countryCode', detectedCountry);
  //           setSelectedCountryCode(detectedCountry);
  //         }
  //       }
  //     }, 1000); // Debounce for 1 second
  //   },
  //   [setValue, watchedCountryCode, setSelectedCountryCode]
  // );

  const handlePhoneNumberChange = useCallback(
    (value) => {
      const cleanedValue = cleanPhoneNumber(value || '');
      const phoneLength = getPhoneNumberLength(watchedCountryCode);
      const limitedValue = cleanedValue.slice(0, phoneLength.max);
      setValue('phone_number', limitedValue);
      // debouncedAutoDetect(cleanedValue); // Auto-detection disabled
    },
    [setValue, watchedCountryCode]
  );

  const handleAlternatePhoneNumberChange = useCallback(
    (value) => {
      const cleanedValue = cleanPhoneNumber(value || '');
      const phoneLength = getPhoneNumberLength(watchedCountryCode);
      const limitedValue = cleanedValue.slice(0, phoneLength.max);
      setValue('alternate_phone_number', limitedValue);
      // debouncedAutoDetect(cleanedValue); // Auto-detection disabled
    },
    [setValue, watchedCountryCode]
  );

  const handleNameChange = useCallback(
    (value) => {
      const cleanedValue = value?.replace(/[^a-zA-Z0-9\s.]/g, '') || '';
      setValue('name', cleanedValue);
    },
    [setValue]
  );

  // Handle country code changes
  const handleCountryCodeChange = useCallback(
    (value) => {
      setSelectedCountryCode(value);
      setValue('countryCode', value);
      // Clear phone numbers when country changes as they may be invalid
      setValue('phone_number', '');
      setValue('alternate_phone_number', '');
    },
    [setValue]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoDetectTimeoutRef.current) {
        clearTimeout(autoDetectTimeoutRef.current);
      }
    };
  }, []);

  const PID = watch('interested_projects');

  // Fetch accommodations for the selected project
  const { data: Alaccommodations } = useQuery({
    queryKey: ['Alaccommodations', PID],
    queryFn: () => getAllAccommodationsByProject(PID),
    enabled: !!PID
  });

  // Handle project change and reset project accommodations
  const handleProjectChange = useCallback(
    (value) => {
      setValue('interested_projects', value);
      // Reset project accommodations when project changes
      setValue('project_accommodations', []);
      // Force a re-render by updating the key
      setProjectAccommodationsKey((prev) => prev + 1);
    },
    [setValue]
  );

  const [selectedProject, setSelectedProject] = useState(null);

  const getSelectedProject = async () => {
    if (!PID) return null;
    try {
      const project = await getProjectByID(PID);
      return project?.measurement_type;
    } catch (error) {
      console.error('Error fetching project:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (PID) {
        const measurementType = await getSelectedProject();
        setSelectedProject(measurementType);
      }
    };

    fetchProject();
  }, [PID]);

  console.log('selectedProject', selectedProject);

  const plotAreaOptions = [
    {
      name: `0-100 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '0-100',
      value: '0-100'
    },
    {
      name: `100-200 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '100-200',
      value: '100-200'
    },
    {
      name: `200-300 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '200-300',
      value: '200-300'
    },
    {
      name: `300 - 500 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '300-500',
      value: '300-500'
    },
    {
      name: `500 - 750 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '500-750',
      value: '500-750'
    },
    {
      name: `750 - 900 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '750-900',
      value: '750-900'
    },
    {
      name: `900 - 1100 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '900-1100',
      value: '900-1100'
    },
    {
      name: `1100 - 1350 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '1100-1350',
      value: '1100-1350'
    },
    {
      name: `1350 - 1600 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '1350-1600',
      value: '1350-1600'
    },
    {
      name: `1600 - 2000 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '1600-2000',
      value: '1600-2000'
    },
    {
      name: `2000 - 2400 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '2000-2400',
      value: '2000-2400'
    },
    {
      name: `2400 - 3000 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '2400-3000',
      value: '2400-3000'
    },
    {
      name: `3000 - 3500 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '3000-3500',
      value: '3000-3500'
    },
    {
      name: `3500 - 4200 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '3500-4200',
      value: '3500-4200'
    },
    {
      name: `4200 - 5000 ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '4200-5000',
      value: '4200-5000'
    },
    {
      name: `5000 - Above ${selectedProject ? `(${selectedProject})` : ''}`,
      id: '5000-above',
      value: '5000-above'
    }
  ];

  return (
    <>
      {/* Lead Selection Form */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Select Lead</span>
      </div>
      <form onSubmit={handleSelectSubmit(onSubmitSelect)} className="flex flex-col gap-4 pt-6">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={100}
            label={'Select Lead'}
            register={selectRegister}
            register_key={'lead_id'}
            type="dropdown-autofilter"
            defaultValuePre={''}
            options={leadOptions}
            onSearchChange={setLeadPickerSearch}
            errors={selectErrors.lead_id}
          />
        </div>
        <div className="w-full flex justify-between items-center gap-4 mt-4 flex-shrink-0">
          <Button
            type="button"
            onClick={() => setIsCreateLeadModalOpen(true)}
            displayType="secondary">
            Create New Lead
          </Button>
          <div className="flex items-center gap-4">
            <Button type="button" onClick={onClose} displayType="secondary">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLeadsLoading}>
              Select
            </Button>
          </div>
        </div>
      </form>

      {/* Create Lead Modal */}
      {isCreateLeadModalOpen && (
        <Modal width={50} name="Create New Lead" onClose={() => setIsCreateLeadModalOpen(false)}>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
              Create New Lead
            </span>
          </div>
          <form onSubmit={handleCreateSubmit(onSubmitCreate)} className="flex flex-col gap-4 pt-6">
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                width={100}
                label={'Name'}
                register={createRegister}
                register_key={'name'}
                type={'text'}
                isRequired={true}
                errors={createErrors.name}
                onType={(text) => handleNameChange(text)}
              />
              <div className="w-[100%] grid grid-cols-[140px_1fr] gap-2 items-end">
                <FormInput
                  width={100}
                  label={'Phone Number'}
                  register={createRegister}
                  register_key={'countryCode'}
                  type={'CountyCodedropdown'}
                  isRequired={true}
                  options={countryCodeOptions}
                  errors={createErrors?.countryCode}
                  onDropdownChange={handleCountryCodeChange}
                  valuePre={watchedCountryCode}
                  defaultValuePre={getDefaultCountryCode()}
                />
                <FormInput
                  width={100}
                  label={''}
                  register={createRegister}
                  register_key={'phone_number'}
                  type={'text'}
                  errors={createErrors?.phone_number}
                  onType={(text) => handlePhoneNumberChange(text)}
                  isRequired={true}
                  hideAstrik={true}
                  placeholder={`Enter (${getPhoneNumberLength(watchedCountryCode).min}-${getPhoneNumberLength(watchedCountryCode).max} digits)`}
                />
              </div>
              <FormInput
                width={100}
                label={'Project Name'}
                register={createRegister}
                register_key={'interested_projects'}
                type={'dropdown'}
                options={transformedData}
                errors={createErrors.interested_projects}
                readOnly={true}
                onDropdownChange={handleProjectChange}
              />
              <div className="w-[100%] flex flex-col gap-1">
                <label className="text-gray-600 mb-1 block">Project Accommodations</label>
                <Controller
                  name="project_accommodations"
                  control={control}
                  render={({ field }) => (
                    <Multiselect
                      ref={multiselectRef}
                      key={projectAccommodationsKey}
                      options={Alaccommodations || []}
                      selectedValues={field.value}
                      onSelect={(selectedList) => field.onChange(selectedList)}
                      onRemove={(selectedList) => field.onChange(selectedList)}
                      displayValue="name"
                      placeholder={PID ? 'Select project accommodations' : 'Select a project first'}
                      disabled={!PID}
                      showCheckbox
                      style={{
                        chips: {
                          background: '#2A565E',
                          color: 'white'
                        },
                        searchBox: {
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '8px',
                          background: PID ? 'white' : '#f5f5f5',
                          color: '#333'
                        },
                        input: {
                          color: '#333'
                        },
                        singleValue: {
                          color: '#333'
                        },
                        placeholder: {
                          color: '#6c757d'
                        },
                        option: {
                          color: '#333',
                          background: 'white'
                        },
                        listItem: {
                          color: '#333',
                          background: 'white'
                        }
                      }}
                    />
                  )}
                />
                {createErrors.project_accommodations && (
                  <span className="text-red-500">
                    {createErrors.project_accommodations.message}
                  </span>
                )}
              </div>
              <div className="w-[100%] flex flex-col gap-1">
                <label className="text-gray-600 mb-1 block">Interested Accomodation</label>
                <Controller
                  name="accomodations"
                  control={control}
                  render={({ field }) => (
                    <Multiselect
                      options={[
                        { name: '1BHK', id: '1BHK', value: '1BHK' },
                        { name: '1.5BHK', id: '1.5BHK', value: '1.5BHK' },
                        { name: '2BHK', id: '2BHK', value: '2BHK' },
                        { name: '2.5BHK', id: '2.5BHK', value: '2.5BHK' },
                        { name: '3BHK', id: '3BHK', value: '3BHK' },
                        { name: '3.5BHK', id: '3.5BHK', value: '3.5BHK' },
                        { name: '4BHK', id: '4BHK', value: '4BHK' },
                        { name: '4.5BHK', id: '4.5BHK', value: '4.5BHK' },
                        { name: '5BHK', id: '5BHK', value: '5BHK' },
                        { name: '5.5BHK', id: '5.5BHK', value: '5.5BHK' },
                        { name: 'Other', id: 'Other', value: 'Other' }
                      ]}
                      selectedValues={field.value}
                      onSelect={(selectedList) => field.onChange(selectedList)}
                      onRemove={(selectedList) => field.onChange(selectedList)}
                      displayValue="name"
                      placeholder="Select Accommodations"
                      showCheckbox
                    />
                  )}
                />
                {createErrors.accomodations && (
                  <span className="text-red-500">{createErrors.accomodations.message}</span>
                )}
              </div>
              <div className="w-[100%] flex flex-col gap-1">
                <label className="text-gray-600 mb-1 block">
                  Interested Plot Area {selectedProject ? `(${selectedProject})` : ''}
                </label>
                <Controller
                  name="plotArea"
                  control={control}
                  render={({ field }) => (
                    <Multiselect
                      options={plotAreaOptions}
                      selectedValues={field.value}
                      onSelect={(selectedList) => field.onChange(selectedList)}
                      onRemove={(selectedList) => field.onChange(selectedList)}
                      displayValue="name"
                      placeholder="Select Plot Area"
                      showCheckbox
                    />
                  )}
                />
                {createErrors.plotArea && (
                  <span className="text-red-500">{createErrors.plotArea.message}</span>
                )}
              </div>

              <FormInput
                width={100}
                label={'Alternate Phone Number'}
                register={createRegister}
                register_key={'alternate_phone_number'}
                type={'text'}
                onType={(text) => handleAlternatePhoneNumberChange(text)}
                errors={createErrors.alternate_phone_number}
                placeholder={`Enter number (${getPhoneNumberLength(watchedCountryCode).min}-${getPhoneNumberLength(watchedCountryCode).max} digits)`}
              />
              <FormInput
                width={100}
                label={'Nick Name'}
                register={createRegister}
                register_key={'reference_name'}
                type={'text'}
                errors={createErrors?.reference_name}
                isRequired={false}
              />
              <FormInput
                width={100}
                label={'Email'}
                register={createRegister}
                register_key={'email'}
                type={'text'}
                errors={createErrors.email}
              />
              <FormInput
                width={100}
                label={'Gender'}
                register={createRegister}
                register_key={'gender'}
                defaultValuePre={''}
                type={'dropdown'}
                options={[
                  { value: '', label: 'Select Gender', disabled: true, selected: true },
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'other', label: 'Other' }
                ]}
                errors={createErrors.gender}
              />
              <FormInput
                width={100}
                label={'Expected Budget '}
                register={createRegister}
                register_key={'expectedBudgetRange'}
                type={'dropdown'}
                options={[
                  { value: '', label: 'Select Budget', disabled: true, selected: true },
                  { value: '0-10', label: '0 - 10 Lacs' },
                  { value: '10-20', label: '10 Lacs - 20 Lacs' },
                  { value: '20-30', label: '20 Lacs - 30 Lacs' },
                  { value: '30-40', label: '30 Lacs - 40 Lacs' },
                  { value: '40-50', label: '40 Lacs - 50 Lacs' },
                  { value: '50-60', label: '50 Lacs - 60 Lacs' },
                  { value: '60-75', label: '60 Lacs - 75 Lacs' },
                  { value: '75-100', label: '75 Lacs - 100 Lacs' },
                  { value: '100-150', label: '100 Lacs - 150 Lacs' },
                  { value: '150-200', label: '150 Lacs - 200 Lacs' },
                  { value: '200-300', label: '200 Lacs - 300 Lacs' },
                  { value: '300-400', label: '300 Lacs - 400 Lacs' },
                  { value: '400-above', label: '400 Lacs - Above' }
                ]}
                errors={createErrors.expectedBudgetRange}
                isRequired={false}
              />
              <FormInput
                width={100}
                label={'Source'}
                register={createRegister}
                register_key={'source'}
                defaultValuePre={''}
                type={'dropdown'}
                isRequired={false}
                options={[
                  { value: '', label: 'Select Source', disabled: true, selected: true },
                  { value: 'Real Estate Brokers/Agents', label: 'Real Estate Brokers/Agents' },
                  { value: 'Channel Partners', label: 'Channel Partners' },
                  { value: 'Property Listing Portals', label: 'Property Listing Portals' },
                  { value: 'Google Ads', label: 'Google Ads' },
                  { value: 'Facebook', label: 'Facebook' },
                  { value: 'Instagram', label: 'Instagram' },
                  { value: 'LinkedIn', label: 'LinkedIn' },
                  { value: 'Company Website', label: 'Company Website' },
                  {
                    value: 'Walk-ins from Project Signage/Hoardings',
                    label: 'Walk-ins from Project Signage/Hoardings'
                  },
                  {
                    value: 'Existing Customer Referrals (Word of Mouth)',
                    label: 'Existing Customer Referrals (Word of Mouth)'
                  },
                  { value: 'Friends/Family', label: 'Friends/Family' },
                  { value: 'Tele-calling Campaigns', label: 'Tele-calling Campaigns' },
                  { value: 'Email or WhatsApp Campaigns', label: 'Email or WhatsApp Campaigns' },
                  { value: 'Offline Events & Exhibitions', label: 'Offline Events & Exhibitions' }
                ]}
                errors={createErrors.source}
              />
              <FormInput
                width={100}
                register={createRegister}
                register_key={'expected_clouser_date'}
                label={'Expected Clouser Date'}
                type={'date'}
                isRequired={false}
              />
              <FormInput
                width={100}
                label={'Occupation'}
                register={createRegister}
                register_key={'occupation'}
                isRequired={false}
                defaultValuePre={''}
                type={'dropdown'}
                options={[
                  { value: '', label: 'Select Occupation', disabled: true, selected: true },
                  ...occupationOptions
                ]}
                errors={createErrors.occupation}
              />
              <FormInput
                width={100}
                label={'Address'}
                register={createRegister}
                register_key={'address'}
                type={'text'}
                errors={createErrors.address}
              />
            </div>
            <div className="flex justify-end items-center space-x-4 mt-4">
              <Button
                type="button"
                onClick={() => setIsCreateLeadModalOpen(false)}
                displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                Create Lead
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
};
